import { useState, useEffect, useCallback, useRef } from 'react'

/**
 * useContainerSize — observes an element's dimensions via ResizeObserver.
 *
 * @param {React.RefObject} elementRef - Ref to the DOM element to observe.
 * @param {Object} [options]
 * @param {number} [options.debounceSpan=0] - Debounce delay in ms. 0 means no debounce (default).
 * @returns {{width: number, height: number}}
 *
 * ResizeObserver already batches notifications per animation frame, so debouncing
 * is unnecessary for most cases. Use debounceSpan only if the consuming component
 * does expensive work on every resize (e.g. complex layout recalculations).
 */
function useContainerSize(elementRef, { debounceSpan = 0 } = {}) {
  const [ size, setSize ] = useState({ width: 0, height: 0 })
  const timerRef = useRef(null)

  const updateSize = useCallback((width, height) => {
    setSize((prev) => {
      if (prev.width === width && prev.height === height) return prev
      return { width, height }
    })
  }, [])

  useEffect(() => {
    const element = elementRef.current
    if (!element) {
      return
    }

    const { clientWidth, clientHeight } = element
    updateSize(clientWidth, clientHeight)

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect

      if (debounceSpan > 0) {
        clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => updateSize(width, height), debounceSpan)
      } else {
        updateSize(width, height)
      }
    })

    observer.observe(element)

    return () => {
      observer.disconnect()
      clearTimeout(timerRef.current)
    }
  }, [ elementRef, debounceSpan, updateSize ])

  return { ...size }
}

export default useContainerSize
